import React from "react";
import { Box, IconButton, Typography, Select, Option } from "@mui/joy";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { PaginationInfo } from "../../../api/assignments/Assignments.responses";

const PaginationControls: React.FC<{
  pagination: PaginationInfo;
  currentPage: number;
  onPageChange: (page: number) => void;
}> = ({ pagination, currentPage, onPageChange }) => {
  const { totalPages, totalItems } = pagination;
  const showPrevious = currentPage > 1;
  const showNext = totalPages > 0 && currentPage < totalPages;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      <IconButton
        size="sm"
        variant="soft"
        color="neutral"
        disabled={!showPrevious}
        onClick={() => onPageChange(currentPage - 1)}
        sx={{ "--IconButton-size": "32px" }}
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography
        level="body-sm"
        sx={{
          minWidth: "5ch",
          textAlign: "center",
          fontWeight: 500,
          lineHeight: "32px",
        }}
      >
        {currentPage} / {totalPages || 1}
      </Typography>
      <IconButton
        size="sm"
        variant="soft"
        color="neutral"
        disabled={!showNext}
        onClick={() => onPageChange(currentPage + 1)}
        sx={{ "--IconButton-size": "32px" }}
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
      <Typography level="body-xs" color="neutral" sx={{ ml: 0.5, lineHeight: "32px" }}>
        ({totalItems})
      </Typography>
    </Box>
  );
};

export const PaginationBar: React.FC<{
  pagination?: PaginationInfo;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (event: React.SyntheticEvent | null, value: number | null) => void;
  pageSizeOptions: readonly number[];
}> = ({ pagination, currentPage, onPageChange, pageSize, onPageSizeChange, pageSizeOptions }) => {
  const shouldShowPagination =
    !!pagination && (pagination.totalPages > 1 || pagination.totalItems > 0 || currentPage > 1);

  if (!shouldShowPagination || !pagination) return null;

  return (
    <Box
      component="footer"
      role="region"
      aria-label="Pagination"
      sx={{
        flexShrink: 0,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        backgroundColor: "background.surface",
        borderTop: "1px solid",
        borderColor: "divider",
        py: 1,
        px: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography level="body-xs" color="neutral" sx={{ whiteSpace: "nowrap" }}>
          Par page
        </Typography>
        <Select
          value={pageSize}
          onChange={onPageSizeChange}
          size="sm"
          sx={{ minWidth: "clamp(60px, 12vw, 90px)", "--Select-minHeight": "32px" }}
          slotProps={{
            listbox: { placement: "top" },
          }}
        >
          {pageSizeOptions.map((size) => (
            <Option key={size} value={size}>
              {size}
            </Option>
          ))}
        </Select>
      </Box>
      <PaginationControls
        pagination={pagination}
        currentPage={currentPage}
        onPageChange={onPageChange}
      />
    </Box>
  );
};
